// src/pages/sessions/components/LiveSession/mobile/PhoneControlsOverlay.jsx
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  MicrophoneIcon,
  VideoCameraIcon,
  PhoneXMarkIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ComputerDesktopIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import {
  MicrophoneIcon as MicrophoneIconSolid,
  VideoCameraIcon as VideoCameraIconSolid,
} from '@heroicons/react/24/solid';

/**
 * A button that looks grayed/locked and shows a tooltip on tap
 * explaining that the teacher has locked this control.
 */
const LockedButton = ({ icon: Icon, label, tooltipText }) => {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const hideTimerRef = useRef(null);

  const showTooltip = () => {
    setTooltipVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setTooltipVisible(false), 2500);
  };

  useEffect(() => {
    if (!tooltipVisible) return;
    const hide = () => setTooltipVisible(false);
    document.addEventListener('pointerdown', hide, { once: true });
    return () => document.removeEventListener('pointerdown', hide);
  }, [tooltipVisible]);

  useEffect(() => () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  }, []);

  return (
    <div className="relative flex flex-col items-center justify-center">
      {tooltipVisible && (
        <div
          className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 z-50
                     bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl
                     border border-gray-700 pointer-events-none"
          style={{ width: 180, textAlign: 'center' }}
        >
          {tooltipText}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </div>
      )}
      <button
        type="button"
        onClick={showTooltip}
        className="w-14 h-14 rounded-full bg-gray-700 opacity-50 flex flex-col items-center justify-center gap-0.5"
        aria-label={tooltipText}
        aria-disabled="true"
      >
        <Icon className="w-5 h-5 text-gray-400" />
        <span className="text-[9px] text-gray-500 font-medium">{label}</span>
      </button>
    </div>
  );
};

/**
 * Grayed-out screen share button for phone.
 * Shows a tooltip on tap explaining it's not supported.
 * Uses the same pattern as MediaControls.jsx for consistency.
 */
const ScreenShareUnsupportedButton = ({ t }) => {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const hideTimerRef = useRef(null);

  const showTooltip = () => {
    setTooltipVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setTooltipVisible(false), 2500);
  };

  useEffect(() => {
    if (!tooltipVisible) return;
    const hide = () => setTooltipVisible(false);
    document.addEventListener('pointerdown', hide, { once: true });
    return () => document.removeEventListener('pointerdown', hide);
  }, [tooltipVisible]);

  useEffect(() => () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  }, []);

  const message = t('controls.screenShareNotSupported', 'Screen sharing is not supported on this device');

  return (
    <div className="relative flex flex-col items-center justify-center">
      {tooltipVisible && (
        <div
          className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 z-50
                     bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl
                     border border-gray-700 pointer-events-none"
          style={{ width: 180, textAlign: 'center' }}
        >
          {message}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </div>
      )}
      <button
        type="button"
        onClick={showTooltip}
        className="w-14 h-14 rounded-full bg-gray-700 opacity-50 flex flex-col items-center justify-center gap-0.5"
        aria-label={message}
        aria-disabled="true"
      >
        <ComputerDesktopIcon className="w-5 h-5 text-gray-400" />
        <span className="text-[9px] text-gray-500 font-medium">{t('controls.share', 'Share')}</span>
      </button>
    </div>
  );
};

/**
 * PhoneControlsOverlay — collapsible bottom sheet
 *
 * Always anchored at the bottom of the video area. Has a visible drag handle
 * and chevron so users always know it's there and can expand it.
 *
 * Collapsed: shows a slim handle bar with mic/camera status indicators
 *            so you can see your state at a glance without expanding
 * Expanded:  shows full mic, camera, flip, leave buttons
 *
 * No auto-hide. No tap-to-reveal. Always present, always discoverable.
 */
const PhoneControlsOverlay = ({
  audioEnabled,
  videoEnabled,
  userMutedAudio,
  userMutedVideo,
  hasUserInteracted,
  canToggleMicrophone,
  canToggleCamera,
  onToggleAudio,
  onToggleVideo,
  onRotateCamera,
  onLeave,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);

  const micMuted = userMutedAudio && hasUserInteracted?.audio;
  const camMuted = userMutedVideo && hasUserInteracted?.video;

  return (
    <div
      className="flex-shrink-0 bg-gray-900/95 backdrop-blur-sm"
      style={{ border: '1px solid rgba(255,255,255,0.08)', borderBottom: 'none' }}
    >
        {/* Handle + collapse/expand row — always visible */}
        <button
          type="button"
          className="w-full flex flex-col items-center pt-2 pb-1 active:bg-white/5 transition-colors rounded-t-2xl"
          onClick={() => setExpanded(prev => !prev)}
          aria-label={expanded ? t('controls.collapseControls', 'Collapse controls') : t('controls.expandControls', 'Expand controls')}
        >
          {/* Drag handle pill */}
          <div className="w-10 h-1 bg-gray-500 rounded-full mb-2" />

          {/* Collapsed state preview — status dots + chevron */}
          {!expanded && (
            <div className="flex items-center gap-3 pb-1">
              {/* Mic status */}
              <div className={`flex items-center gap-1.5 text-xs font-medium ${micMuted ? 'text-red-400' : 'text-green-400'}`}>
                {micMuted
                  ? <MicrophoneIconSolid className="w-3.5 h-3.5" />
                  : <MicrophoneIcon className="w-3.5 h-3.5" />
                }
                <span>{micMuted ? t('controls.muted', 'Muted') : t('controls.live', 'Live')}</span>
              </div>
              <div className="w-px h-3 bg-gray-600" />
              {/* Camera status */}
              <div className={`flex items-center gap-1.5 text-xs font-medium ${camMuted ? 'text-red-400' : 'text-green-400'}`}>
                {camMuted
                  ? <VideoCameraIconSolid className="w-3.5 h-3.5" />
                  : <VideoCameraIcon className="w-3.5 h-3.5" />
                }
                <span>{camMuted ? t('controls.cameraOff', 'Off') : t('controls.cameraOn', 'On')}</span>
              </div>
              <div className="w-px h-3 bg-gray-600" />
              <ChevronUpIcon className="w-4 h-4 text-gray-400" />
            </div>
          )}

          {/* Expanded state — just show chevron down */}
          {expanded && (
            <ChevronDownIcon className="w-4 h-4 text-gray-500 mb-1" />
          )}
        </button>

        {/* Control buttons — only when expanded */}
        {expanded && (
          <div
            className="flex items-center justify-center gap-4 px-4 pb-3"
            style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}
          >
            {/* Microphone */}
            {!canToggleMicrophone && audioEnabled ? (
              <LockedButton
                icon={LockClosedIcon}
                label={t('controls.mute', 'Mute')}
                tooltipText={t('controls.microphoneLockedByTeacher', 'Your microphone is locked by the teacher')}
              />
            ) : (
              <button
                type="button"
                onClick={onToggleAudio}
                className={`w-14 h-14 rounded-full flex flex-col items-center justify-center gap-0.5 transition-all active:scale-95
                  ${micMuted ? 'bg-red-600' : 'bg-gray-700'}
                `}
                aria-label={audioEnabled ? t('controls.muteMicrophone') : t('controls.unmuteMicrophone')}
              >
                {audioEnabled
                  ? <MicrophoneIcon className="w-5 h-5 text-white" />
                  : <MicrophoneIconSolid className="w-5 h-5 text-white" />
                }
                <span className="text-[9px] text-white/70 font-medium">
                  {micMuted ? t('controls.unmute', 'Unmute') : t('controls.mute', 'Mute')}
                </span>
              </button>
            )}

            {/* Camera */}
            {!canToggleCamera && videoEnabled ? (
              <LockedButton
                icon={LockClosedIcon}
                label={t('controls.stop', 'Stop')}
                tooltipText={t('controls.cameraLockedByTeacher', 'Your camera is locked by the teacher')}
              />
            ) : (
              <button
                type="button"
                onClick={onToggleVideo}
                className={`w-14 h-14 rounded-full flex flex-col items-center justify-center gap-0.5 transition-all active:scale-95
                  ${camMuted ? 'bg-red-600' : 'bg-gray-700'}
                `}
                aria-label={videoEnabled ? t('controls.stopCamera') : t('controls.startCamera')}
              >
                {videoEnabled
                  ? <VideoCameraIcon className="w-5 h-5 text-white" />
                  : <VideoCameraIconSolid className="w-5 h-5 text-white" />
                }
                <span className="text-[9px] text-white/70 font-medium">
                  {camMuted ? t('controls.start', 'Start') : t('controls.stop', 'Stop')}
                </span>
              </button>
            )}

            {/* Flip camera — only when video is on */}
            {videoEnabled && (
              <button
                type="button"
                onClick={onRotateCamera}
                className="w-14 h-14 rounded-full bg-gray-700 flex flex-col items-center justify-center gap-0.5 transition-all active:scale-95"
                aria-label={t('controls.flipCamera')}
              >
                <ArrowPathIcon className="w-5 h-5 text-white" />
                <span className="text-[9px] text-white/70 font-medium">{t('controls.flip', 'Flip')}</span>
              </button>
            )}

            {/* Screen share — grayed out, not supported on phone */}
            <ScreenShareUnsupportedButton t={t} />

            {/* Leave */}
            <button
              type="button"
              onClick={onLeave}
              className="w-14 h-14 rounded-full bg-red-600 flex flex-col items-center justify-center gap-0.5 transition-all active:scale-95"
              aria-label={t('controls.leaveSession')}
            >
              <PhoneXMarkIcon className="w-5 h-5 text-white" />
              <span className="text-[9px] text-white/70 font-medium">{t('controls.leave', 'Leave')}</span>
            </button>
          </div>
        )}
      </div>
  );
};

export default PhoneControlsOverlay;
